import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { adminListBlacklist, adminRemoveBlacklist, BlacklistEntry } from '../../api/payment/brand-sanction.api';
import { isSuperAdmin } from '../../utils/admin-permissions';
import { apiErrorMessage } from '../../utils/api-error-message';
import { formatVnDate } from '../../utils/brand-sanction';
import { validateAdminNote } from '../../utils/brand-sanction-admin';
import AdminTotpInput from './admin-totp-input';

const KIND_LABEL: Record<string, string> = {
  TaxCode: 'Mã số thuế',
  IdentityFingerprint: 'CCCD chủ tài khoản',
};

/** Tab "Danh sách đen": mã số thuế và CCCD (chỉ hiện 8 ký tự đầu của dấu vân tay) bị chặn đặt đơn. */
const AdminBlacklistPanel: React.FC = () => {
  const [items, setItems] = useState<BlacklistEntry[] | null>(null);
  const [removing, setRemoving] = useState<BlacklistEntry | null>(null);
  const [reason, setReason] = useState('');
  const [totp, setTotp] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminListBlacklist()
      .then(setItems)
      .catch((e) => {
        setItems([]);
        toast.error(apiErrorMessage(e, 'Không tải được danh sách đen.'));
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const confirmRemove = async () => {
    if (!removing) return;
    try {
      setBusy(true);
      await adminRemoveBlacklist(removing.id, reason.trim(), totp);
      toast.success('Đã gỡ khỏi danh sách đen.');
      setRemoving(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Gỡ khỏi danh sách đen thất bại.'));
    } finally {
      setBusy(false);
    }
  };

  if (items === null) return <p className="text-gray-500">Đang tải…</p>;
  if (items.length === 0) return <p className="text-gray-500">Danh sách đen đang trống.</p>;

  return (
    <div className="space-y-3">
      {items.map((it) => (
        <div
          key={it.id}
          data-testid="blacklist-entry"
          className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4 text-sm dark:border-gray-700 dark:bg-boxdark"
        >
          <div className="min-w-0">
            <div className="font-semibold dark:text-white">
              {KIND_LABEL[it.kind] ?? 'Định danh'}: <span className="font-mono">{it.valueDisplay}</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {it.businessName} · đưa vào ngày {formatVnDate(it.createdAt)}
            </div>
            <div className="mt-1 text-gray-700 dark:text-gray-200">Lý do lần phạt gốc: {it.sanctionReason}</div>
          </div>
          {isSuperAdmin() && (
            <button
              type="button"
              onClick={() => {
                setRemoving(it);
                setReason('');
                setTotp('');
              }}
              className="shrink-0 rounded bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-700"
            >
              Gỡ khỏi danh sách đen
            </button>
          )}
        </div>
      ))}

      {removing && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4" onClick={() => setRemoving(null)}>
          <div
            className="w-full max-w-[440px] space-y-3 rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-lg font-semibold text-slate-800 dark:text-white">Gỡ khỏi danh sách đen</h4>
            <p className="text-sm text-slate-500 dark:text-slate-300">
              {KIND_LABEL[removing.kind] ?? 'Định danh'} <b className="font-mono">{removing.valueDisplay}</b> sẽ lại mở được tài khoản
              đặt đơn.
            </p>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
              Lý do gỡ (10–500 ký tự) <span className="text-red-500">*</span>
              <textarea
                aria-label="Lý do gỡ"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                rows={3}
                className="mt-1 w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#0057FF] dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              />
            </label>
            <AdminTotpInput value={totp} onChange={setTotp} />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setRemoving(null)}
                className="h-10 min-w-[90px] rounded-md border border-gray-300 px-4 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                Huỷ
              </button>
              <button
                type="button"
                disabled={busy || totp.length !== 6 || !!validateAdminNote(reason)}
                onClick={confirmRemove}
                className="h-10 min-w-[130px] rounded-md bg-emerald-600 px-4 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Gỡ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlacklistPanel;
